// src/components/AIAssistant.js — /my-ai-assistants, the account's AI agents.
// Header (count, default agent) → « À vérifier » (agents without
// instructions) → tiles over 30 days (runs, drafts approved, carrier messages
// validated, statuses the team took back: GET /api/ai-runs/assistants/{id}/
// versions per agent, summed) → the agents table (what each answers on, its
// version, tools, knowledge sources, activity). A row opens
// /ai-assistants?assistantId=<id> (the backend's ticket journey and the
// copilot link there). « ⋯ » binds a mailbox, channels or a brand.
// API: GET /api/users/user, /api/ai-assistants, /api/tools,
// /api/users/user/emails, /api/users/available-channels, /api/brands/list,
// /api/files/list (sources per agent); POST /api/ai-assistants,
// /api/ai-assistants/associate-email | remove-email-association |
// associate-channels | associate-brand | remove-brand-association.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BookOpen, Bot, ChevronRight, ListChecks, Mail, MessagesSquare, Plus, RefreshCw, ShieldCheck, Sparkles, Tag,
  TriangleAlert, Truck, Unlink,
} from 'lucide-react';
import {
  Badge, Banner, Card, ConfirmDialog, EmptyState, Field, Kpi, Meter, Modal, OverflowMenu, PageHeader, Switch, Toast,
} from '../design/ui';
import ToolChecklist from './AIAssistantTools';
import {
  activeChannels, agentTools, agentsAttention, authHeaders, channelLabel, countByAgent, currentVersionEntry,
  formatCount, formatPercent, formatRelative, formatShortDay, hasInstructions, isTestChannel, summarizeAccount,
  summarizeVersions,
} from './aiAgentsShared';
import { DEMO_AGENTS, DEMO_CHANNELS, DEMO_EMAILS, DEMO_TOOLS } from './aiAgentsDemo';
import './AIAssistant.css';

const METRICS_DAYS = 30;

const AIAgentsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [user, setUser] = useState(undefined); // undefined = loading
  const [agents, setAgents] = useState(null); // null = loading
  const [listError, setListError] = useState(null);
  const [tools, setTools] = useState([]);
  const [userEmails, setUserEmails] = useState({ gmail_email: null, outlook_emails: [] });
  const [availableChannels, setAvailableChannels] = useState({});
  const [brands, setBrands] = useState([]);
  const [metrics, setMetrics] = useState({}); // agentId → summary | null (failed)
  const [knowledgeCounts, setKnowledgeCounts] = useState(null);
  const [toast, setToast] = useState(null);

  // Bindings
  const [emailFor, setEmailFor] = useState(null); // agent
  const [emailChoice, setEmailChoice] = useState('');
  const [channelsFor, setChannelsFor] = useState(null);
  const [channelChoice, setChannelChoice] = useState({});
  const [brandFor, setBrandFor] = useState(null);
  const [brandChoice, setBrandChoice] = useState('');
  const [confirm, setConfirm] = useState(null); // { kind: 'email' | 'brand', agent }
  const [binding, setBinding] = useState(false);
  const [bindingError, setBindingError] = useState(null);

  // Creation
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPrompt, setNewPrompt] = useState('');
  const [newToolIds, setNewToolIds] = useState([]);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  const demo = Boolean(user && user.role === 'experience');

  const showToast = (tone, text) => setToast({ tone, text });
  useEffect(() => {
    if (!toast || toast.tone === 'danger') return undefined;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  const signOutOn = useCallback((error) => {
    if (error.response && (error.response.status === 401 || error.response.status === 422)) {
      localStorage.removeItem('access_token');
      navigate('/');
      return true;
    }
    return false;
  }, [navigate]);

  const fetchAgentsAndTools = useCallback(async () => {
    setListError(null);
    try {
      const [assistantsResponse, toolsResponse] = await Promise.all([
        axios.get('/api/ai-assistants', { headers: authHeaders() }),
        axios.get('/api/tools', { headers: authHeaders() }),
      ]);
      setAgents(Array.isArray(assistantsResponse.data) ? assistantsResponse.data : []);
      setTools(Array.isArray(toolsResponse.data) ? toolsResponse.data : []);
    } catch (error) {
      console.error('Failed to fetch assistants and tools:', error);
      if (signOutOn(error)) return;
      setListError(t('aiAgentsUi.list.loadError', 'Impossible de charger les agents.'));
      setAgents((previous) => previous || []);
    }
  }, [signOutOn, t]);

  const fetchSideData = useCallback(async () => {
    const calls = [
      axios.get('/api/users/user/emails', { headers: authHeaders() })
        .then((response) => setUserEmails(response.data || { gmail_email: null, outlook_emails: [] }))
        .catch((error) => console.error('Failed to fetch user emails:', error)),
      axios.get('/api/users/available-channels', { headers: authHeaders() })
        .then((response) => setAvailableChannels((response.data && response.data.channels) || {}))
        .catch((error) => console.error('Failed to fetch available channels:', error)),
      axios.get('/api/brands/list', { headers: authHeaders() })
        .then((response) => setBrands(Array.isArray(response.data) ? response.data : []))
        .catch((error) => console.error('Failed to fetch available brands:', error)),
      axios.get('/api/files/list', { headers: authHeaders() })
        .then((response) => setKnowledgeCounts(countByAgent((response.data && response.data.files) || [])))
        .catch(() => setKnowledgeCounts(null)),
    ];
    await Promise.all(calls);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get('/api/users/user', { headers: authHeaders() });
        if (cancelled) return;
        const fetched = response.data;
        setUser(fetched);
        if (fetched && fetched.role === 'experience') {
          setAgents(DEMO_AGENTS);
          setTools(DEMO_TOOLS);
          setUserEmails(DEMO_EMAILS);
          setAvailableChannels(DEMO_CHANNELS);
          return;
        }
        await Promise.all([fetchAgentsAndTools(), fetchSideData()]);
      } catch (error) {
        console.error('Failed to fetch user data:', error);
        if (cancelled || signOutOn(error)) return;
        setUser(null);
        setAgents([]);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, fetchAgentsAndTools, fetchSideData, signOutOn]);

  // What each agent did over 30 days (one GET per agent, after the list).
  useEffect(() => {
    if (!agents || demo) return undefined;
    let cancelled = false;
    agents.forEach((agent) => {
      const key = agent.assistant_id || agent._id;
      if (!key) return;
      axios.get(`/api/ai-runs/assistants/${key}/versions?days=${METRICS_DAYS}`, { headers: authHeaders() })
        .then((response) => { if (!cancelled) setMetrics((previous) => ({ ...previous, [agent._id]: summarizeVersions(response.data) })); })
        .catch(() => { if (!cancelled) setMetrics((previous) => ({ ...previous, [agent._id]: null })); });
    });
    return () => { cancelled = true; };
  }, [agents, demo]);

  const refresh = () => {
    setMetrics({});
    fetchAgentsAndTools();
    fetchSideData();
  };

  const openAgent = (agent) => navigate(`/ai-assistants?assistantId=${agent._id}`);

  // ---- bindings ---------------------------------------------------------------

  const validEmails = [userEmails.gmail_email, ...(userEmails.outlook_emails || [])].filter(Boolean);
  const bindableChannels = Object.entries(availableChannels).filter(([name]) => !isTestChannel(name));

  const updateAgent = (agentId, change) => setAgents((previous) => (previous || []).map((agent) => (agent._id === agentId ? change(agent) : agent)));

  const closeBindings = () => {
    if (binding) return;
    setEmailFor(null);
    setChannelsFor(null);
    setBrandFor(null);
    setBindingError(null);
  };

  const runBinding = async (request, apply, successText, close = true) => {
    setBinding(true);
    setBindingError(null);
    try {
      if (!demo) await request();
      apply();
      if (close) {
        setEmailFor(null);
        setChannelsFor(null);
        setBrandFor(null);
      }
      setConfirm(null);
      showToast('success', successText);
    } catch (error) {
      console.error('Agent binding failed:', error);
      const detail = error.response && error.response.data && error.response.data.detail;
      const text = typeof detail === 'string' ? detail : t('aiAgentsUi.bindings.error', 'L’enregistrement a échoué. Réessayez.');
      if (confirm) {
        setConfirm(null);
        showToast('danger', text);
      } else {
        setBindingError(text);
      }
    } finally {
      setBinding(false);
    }
  };

  const openEmail = (agent) => {
    setEmailFor(agent);
    setEmailChoice(agent.associated_email || validEmails[0] || '');
    setBindingError(null);
  };

  const saveEmail = () => runBinding(
    () => axios.post('/api/ai-assistants/associate-email', { email: emailChoice, assistant_id: emailFor._id }, { headers: authHeaders() }),
    () => updateAgent(emailFor._id, (agent) => ({ ...agent, associated_email: emailChoice })),
    t('aiAgentsUi.bindings.emailSaved', '{{email}} est confiée à « {{name}} ».', { email: emailChoice, name: emailFor.name }),
  );

  const removeEmail = (agent) => runBinding(
    () => axios.post('/api/ai-assistants/remove-email-association', { email: agent.associated_email, assistant_id: agent._id }, { headers: authHeaders() }),
    () => updateAgent(agent._id, (current) => {
      const next = { ...current };
      delete next.associated_email;
      return next;
    }),
    t('aiAgentsUi.bindings.emailRemoved', 'Adresse retirée de « {{name}} ».', { name: agent.name }),
  );

  const openChannels = (agent) => {
    setChannelsFor(agent);
    setChannelChoice({ ...(agent.associated_channels || {}) });
    setBindingError(null);
  };

  const saveChannels = () => runBinding(
    () => axios.post('/api/ai-assistants/associate-channels', { assistant_id: channelsFor._id, channels: channelChoice }, { headers: authHeaders() }),
    () => updateAgent(channelsFor._id, (agent) => ({ ...agent, associated_channels: channelChoice })),
    t('aiAgentsUi.bindings.channelsSaved', 'Canaux de « {{name}} » enregistrés.', { name: channelsFor.name }),
  );

  const openBrand = (agent) => {
    setBrandFor(agent);
    setBrandChoice(agent.associated_brand_id || (brands[0] && brands[0].id) || '');
    setBindingError(null);
  };

  const saveBrand = () => runBinding(
    () => axios.post('/api/ai-assistants/associate-brand', { brand_id: brandChoice, assistant_id: brandFor._id }, { headers: authHeaders() }),
    () => updateAgent(brandFor._id, (agent) => ({ ...agent, associated_brand_id: brandChoice })),
    t('aiAgentsUi.bindings.brandSaved', 'Marque enregistrée pour « {{name}} ».', { name: brandFor.name }),
  );

  const removeBrand = (agent) => runBinding(
    () => axios.post('/api/ai-assistants/remove-brand-association', { brand_id: agent.associated_brand_id, assistant_id: agent._id }, { headers: authHeaders() }),
    () => updateAgent(agent._id, (current) => {
      const next = { ...current };
      delete next.associated_brand_id;
      return next;
    }),
    t('aiAgentsUi.bindings.brandRemoved', 'Marque retirée de « {{name}} ».', { name: agent.name }),
  );

  // ---- creation ---------------------------------------------------------------

  const openCreate = () => {
    setNewName('');
    setNewPrompt('');
    setNewToolIds([]);
    setCreateError(null);
    setShowCreate(true);
  };

  const closeCreate = () => {
    if (creating) return;
    setShowCreate(false);
  };

  const handleCreate = async (event) => {
    if (event) event.preventDefault();
    if (!newName.trim()) {
      setCreateError(t('aiAgentsUi.create.nameRequired', 'Le nom est obligatoire.'));
      return;
    }
    setCreating(true);
    setCreateError(null);
    const name = newName;
    try {
      if (demo) {
        const now = new Date().toISOString();
        setAgents((previous) => [...(previous || []), {
          _id: `exp_asst_${Date.now()}`, name, prompt: newPrompt || 'Default demo prompt.', selected_tool_ids: newToolIds,
          is_running: false, created_at: now, updated_at: now,
        }]);
      } else {
        await axios.post('/api/ai-assistants', { name, prompt: newPrompt, selected_tool_ids: newToolIds }, { headers: authHeaders() });
        // The POST answers { message, id }: the list is read again for the document.
        await fetchAgentsAndTools();
      }
      setShowCreate(false);
      showToast('success', t('aiAgentsUi.create.done', 'Agent « {{name}} » créé.', { name: name.trim() }));
    } catch (error) {
      console.error('Failed to create AI assistant:', error);
      const detail = error.response && error.response.data && error.response.data.detail;
      setCreateError(typeof detail === 'string' ? detail : t('aiAgentsUi.create.error', 'La création de l’agent a échoué.'));
    } finally {
      setCreating(false);
    }
  };

  // ---- derived ----------------------------------------------------------------

  const loading = agents === null;
  const list = useMemo(() => agents || [], [agents]);
  const defaultAgent = list.find((agent) => agent.is_running);
  const attention = useMemo(() => agentsAttention(list), [list]);
  const metricsReady = !demo && list.length > 0 && list.every((agent) => metrics[agent._id] !== undefined);
  const account = useMemo(
    () => (metricsReady ? summarizeAccount(list.map((agent) => metrics[agent._id])) : null),
    [metricsReady, list, metrics],
  );
  const brandName = (id) => (brands.find((brand) => brand.id === id) || {}).name;

  const subtitle = loading
    ? t('aiAgentsUi.list.subtitleLoading', 'Les agents qui traitent vos tickets.')
    : !list.length
      ? t('aiAgentsUi.list.subtitleEmpty', 'Un agent lit les tickets, répond, contacte les transporteurs et choisit le statut.')
      : defaultAgent
        ? t('aiAgentsUi.list.subtitle', '{{count}} agents · « {{name}} » répond par défaut', { count: list.length, name: defaultAgent.name })
        : t('aiAgentsUi.list.subtitleNoDefault', '{{count}} agents · aucun agent par défaut', { count: list.length });

  // ---- zones ------------------------------------------------------------------

  const renderAttention = () => {
    if (!attention.length) return null;
    return (
      <Card
        icon={TriangleAlert}
        iconTone="warning"
        title={t('aiAgentsUi.attention.title', 'À vérifier')}
        badge={<Badge tone="warning">{attention.length}</Badge>}
        flush
        className="agents-v2__attention"
      >
        <ul className="u-list">
          {attention.map((item) => (
            <li className="u-row" key={item.key}>
              <Badge tone="warning" dot>{t('aiAgentsUi.attention.watch', 'À surveiller')}</Badge>
              <div className="u-row__main">
                <div className="u-row__title">{t('aiAgentsUi.attention.noPrompt', '« {{name}} » n’a pas d’instructions', { name: item.name })}</div>
                <div className="u-row__meta">{t('aiAgentsUi.attention.noPromptDetail', 'Sans instructions, l’agent ne sait pas comment traiter vos tickets.')}</div>
              </div>
              <div className="u-row__side">
                <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => navigate(`/ai-assistants?assistantId=${item.agentId}`)}>
                  {t('aiAgentsUi.see', 'Voir')}
                  <ChevronRight aria-hidden="true" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      </Card>
    );
  };

  // Four tiles over 30 days (the agent count is in the header).
  const renderKpis = () => {
    if (demo || (!loading && !list.length)) return null;
    if (loading || !metricsReady) {
      return (
        <div className="u-kpi-row agents-v2__kpis" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => <span key={i} className="u-skeleton agents-v2__kpi-skeleton" />)}
        </div>
      );
    }
    if (!account) return null;
    const period = t('aiAgentsUi.kpi.period', 'sur {{days}} jours', { days: METRICS_DAYS });
    return (
      <div className="u-kpi-row agents-v2__kpis">
        <Kpi
          icon={Sparkles}
          label={t('aiAgentsUi.kpi.runsLong', 'Traitements de l’IA')}
          value={formatCount(account.runs)}
          context={t('aiAgentsUi.kpi.runsAgents', '{{period}} · {{count}} agents actifs', { period, count: account.activeAgents })}
        />
        <Kpi
          icon={MessagesSquare}
          tone={account.drafts.rate === null ? 'neutral' : 'success'}
          label={t('aiAgentsUi.kpi.drafts', 'Brouillons approuvés')}
          value={formatPercent(account.drafts.rate)}
          context={account.drafts.reviewed
            ? t('aiAgentsUi.kpi.draftsContext', '{{edited}} corrigés sur {{reviewed}} relus', { edited: formatCount(account.drafts.edited), reviewed: formatCount(account.drafts.reviewed) })
            : t('aiAgentsUi.kpi.noneReviewed', 'aucun relu {{period}}', { period })}
          meter={account.drafts.reviewed ? { value: account.drafts.approved, max: account.drafts.reviewed, tone: 'success', label: t('aiAgentsUi.kpi.drafts', 'Brouillons approuvés') } : undefined}
        />
        <Kpi
          icon={Truck}
          tone={account.carrier.rate === null ? 'neutral' : 'success'}
          label={t('aiAgentsUi.kpi.carrierLong', 'Messages transporteur')}
          value={formatPercent(account.carrier.rate)}
          context={account.carrier.reviewed
            ? t('aiAgentsUi.kpi.carrierContext', 'validés tels quels, sur {{reviewed}} relus', { reviewed: formatCount(account.carrier.reviewed) })
            : t('aiAgentsUi.kpi.noneReviewed', 'aucun relu {{period}}', { period })}
          meter={account.carrier.reviewed ? { value: account.carrier.approved, max: account.carrier.reviewed, tone: 'success', label: t('aiAgentsUi.kpi.carrierLong', 'Messages transporteur') } : undefined}
        />
        <Kpi
          icon={ListChecks}
          tone="neutral"
          label={t('aiAgentsUi.kpi.overrides', 'Statuts repris')}
          value={formatCount(account.statusOverrides)}
          context={t('aiAgentsUi.kpi.overridesContext', 'par votre équipe après l’IA, {{period}}', { period })}
        />
      </div>
    );
  };

  // First line: the mailbox (or the channels); below: channels and brand.
  const respondsOn = (agent) => {
    const channels = activeChannels(agent).map((name) => channelLabel(t, name));
    const brand = agent.associated_brand_id ? brandName(agent.associated_brand_id) : null;
    const main = agent.associated_email || channels.join(' · ');
    const sub = [agent.associated_email ? channels.join(' · ') : '', brand ? t('aiAgentsUi.list.brand', 'Marque {{name}}', { name: brand }) : '']
      .filter(Boolean).join(' · ');
    if (!main) {
      return (
        <>
          <span className="u-muted">
            {agent.is_running
              ? t('aiAgentsUi.list.respondsDefault', 'Tout ce qui n’a pas d’agent attitré')
              : t('aiAgentsUi.list.respondsNone', 'Aucun canal attitré')}
          </span>
          {sub && <span className="u-cell-sub">{sub}</span>}
        </>
      );
    }
    return (
      <>
        <span title={main}>{main}</span>
        {sub && <span className="u-cell-sub" title={sub}>{sub}</span>}
      </>
    );
  };

  const renderTable = () => {
    if (loading) {
      return (
        <div className="agents-v2__skeleton" aria-busy="true" aria-label={t('aiAgentsUi.list.loading', 'Chargement des agents')}>
          {Array.from({ length: 4 }, (_, i) => <span key={i} className="u-skeleton" />)}
        </div>
      );
    }
    if (!list.length) {
      if (listError) return null;
      return (
        <EmptyState
          icon={Bot}
          title={t('aiAgentsUi.list.emptyTitle', 'Aucun agent pour l’instant')}
          action={(
            <button type="button" className="u-btn u-btn--primary u-btn--sm" onClick={openCreate}>
              <Plus aria-hidden="true" />
              {t('aiAgentsUi.list.create', 'Créer un agent')}
            </button>
          )}
        >
          {t('aiAgentsUi.list.emptyHint', 'Créez un agent : il lira vos tickets, répondra, contactera les transporteurs et choisira le statut avec ses outils.')}
        </EmptyState>
      );
    }
    return (
      <div className="u-table-scroll">
        <table className="u-table agents-v2__table">
          <thead>
            <tr>
              <th style={{ width: '28%' }}>{t('aiAgentsUi.list.colAgent', 'Agent')}</th>
              <th style={{ width: '21%' }}>{t('aiAgentsUi.list.colResponds', 'Répond sur')}</th>
              <th style={{ width: '13%' }}>{t('aiAgentsUi.list.colEquipment', 'Outils')}</th>
              <th style={{ width: '15%' }}>{t('aiAgentsUi.list.colActivity', 'Activité · 30 j')}</th>
              <th style={{ width: '17%' }}>{t('aiAgentsUi.list.colDrafts', 'Brouillons approuvés')}</th>
              <th style={{ width: '6%' }} aria-label={t('aiAgentsUi.actions', 'Actions')} />
            </tr>
          </thead>
          <tbody>
            {list.map((agent) => {
              const version = currentVersionEntry(agent);
              const { selected, missing } = agentTools(agent, tools);
              const summary = metrics[agent._id];
              const sources = knowledgeCounts ? knowledgeCounts[agent._id] || 0 : null;
              const menu = [
                validEmails.length > 0 && { label: t('aiAgentsUi.bindings.email', 'Confier une boîte mail'), icon: Mail, onClick: () => openEmail(agent) },
                bindableChannels.length > 0 && { label: t('aiAgentsUi.bindings.channels', 'Choisir les canaux'), icon: MessagesSquare, onClick: () => openChannels(agent) },
                !demo && brands.length > 0 && { label: t('aiAgentsUi.bindings.brand', 'Associer une marque'), icon: Tag, onClick: () => openBrand(agent) },
                (agent.associated_email || (agent.associated_brand_id && !demo)) && { separator: true },
                agent.associated_email && { label: t('aiAgentsUi.bindings.removeEmail', 'Retirer la boîte mail'), icon: Unlink, danger: true, onClick: () => setConfirm({ kind: 'email', agent }) },
                !demo && agent.associated_brand_id && { label: t('aiAgentsUi.bindings.removeBrand', 'Retirer la marque'), icon: Unlink, danger: true, onClick: () => setConfirm({ kind: 'brand', agent }) },
              ];
              return (
                <tr
                  key={agent._id || agent.name}
                  className="agents-v2__row"
                  tabIndex={0}
                  onClick={() => openAgent(agent)}
                  onKeyDown={(event) => {
                    if (event.target !== event.currentTarget) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      openAgent(agent);
                    }
                  }}
                >
                  <td title={agent.name}>
                    <span className="agents-v2__name">
                      <span className="agents-v2__name-text">{agent.name}</span>
                      {agent.is_running && <Badge tone="accent">{t('aiAgentsUi.default', 'Par défaut')}</Badge>}
                    </span>
                    <span className="u-cell-sub">
                      {!hasInstructions(agent)
                        ? t('aiAgentsUi.list.noInstructions', 'Sans instructions')
                        : version
                          ? t('aiAgentsUi.list.version', 'Version {{version}} · {{date}}', { version: version.version, date: formatShortDay(version.changed_at) })
                          : t('aiAgentsUi.list.updated', 'Modifié le {{date}}', { date: formatShortDay(agent.updated_at) })}
                    </span>
                  </td>
                  <td>{respondsOn(agent)}</td>
                  <td>
                    <span className="u-num">{t('aiAgentsUi.list.tools', '{{count}} outils', { count: selected.length })}</span>
                    <span className="u-cell-sub">
                      {missing.length > 0
                        ? t('aiAgentsUi.list.toolsMissing', '{{count}} indisponibles', { count: missing.length })
                        : sources !== null
                          ? t('aiAgentsUi.list.sources', '{{count}} sources', { count: sources })
                          : null}
                    </span>
                  </td>
                  <td>
                    {summary === undefined && !demo ? <span className="u-skeleton agents-v2__cell-skeleton" aria-hidden="true" /> : summary ? (
                      <>
                        <span className="u-num">{t('aiAgentsUi.list.runs', '{{value}} traitements', { count: summary.runs, value: formatCount(summary.runs) })}</span>
                        <span className="u-cell-sub">
                          {summary.lastRunAt
                            ? t('aiAgentsUi.list.lastRun', 'dernier : {{when}}', { when: formatRelative(summary.lastRunAt) })
                            : t('aiAgentsUi.list.noRun', 'aucun sur la période')}
                        </span>
                      </>
                    ) : <span className="u-muted">—</span>}
                  </td>
                  <td>
                    {summary && summary.drafts.reviewed > 0 ? (
                      <span className="agents-v2__rate">
                        <span className="agents-v2__rate-value u-num">{formatPercent(summary.drafts.rate)}</span>
                        <span className="agents-v2__rate-meter">
                          <Meter value={summary.drafts.approved} max={summary.drafts.reviewed} tone="success" label={t('aiAgentsUi.list.colDrafts', 'Brouillons approuvés')} />
                          <span className="u-cell-sub">{t('aiAgentsUi.list.reviewed', 'sur {{value}} relus', { count: summary.drafts.reviewed, value: formatCount(summary.drafts.reviewed) })}</span>
                        </span>
                      </span>
                    ) : <span className="u-muted">{summary ? t('aiAgentsUi.list.noneReviewed', 'aucun relu') : '—'}</span>}
                  </td>
                  <td className="agents-v2__actions" onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
                    <AgentMenu items={menu} label={t('aiAgentsUi.list.actions', 'Actions de l’agent')} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // ---- modals -----------------------------------------------------------------

  const renderEmailModal = () => emailFor && (
    <Modal
      icon={Mail}
      title={t('aiAgentsUi.bindings.emailTitle', 'Confier une boîte mail à « {{name}} »', { name: emailFor.name })}
      description={t('aiAgentsUi.bindings.emailDesc', 'Les e-mails reçus sur cette adresse sont traités par cet agent en priorité.')}
      onClose={closeBindings}
      closeLabel={t('common.close', 'Fermer')}
      footer={(
        <>
          <button type="button" className="u-btn u-btn--secondary" onClick={closeBindings} disabled={binding}>{t('common.cancel', 'Annuler')}</button>
          <button type="button" className="u-btn u-btn--primary" onClick={saveEmail} disabled={binding || !emailChoice || emailChoice === emailFor.associated_email}>
            {t('aiAgentsUi.bindings.emailSubmit', 'Confier cette adresse')}
          </button>
        </>
      )}
    >
      <div className="u-form">
        {bindingError && <Banner tone="danger">{bindingError}</Banner>}
        <fieldset className="agents-v2__choices">
          <legend className="u-label">{t('aiAgentsUi.bindings.emailLegend', 'Adresse')}</legend>
          {validEmails.map((email) => (
            <label key={email} className={`agents-v2__choice${emailChoice === email ? ' is-selected' : ''}`}>
              <input type="radio" name="agent-email" value={email} checked={emailChoice === email} onChange={() => setEmailChoice(email)} disabled={binding} />
              <span className="agents-v2__choice-text">{email}</span>
              {emailFor.associated_email === email && <Badge tone="success">{t('aiAgentsUi.bindings.current', 'Actuelle')}</Badge>}
            </label>
          ))}
        </fieldset>
      </div>
    </Modal>
  );

  const channelRows = channelsFor
    ? Object.keys({ ...availableChannels, ...(channelsFor.associated_channels || {}) })
      .filter((name) => !isTestChannel(name) && (availableChannels[name] || channelChoice[name]))
    : [];

  const renderChannelsModal = () => channelsFor && (
    <Modal
      icon={MessagesSquare}
      title={t('aiAgentsUi.bindings.channelsTitle', 'Canaux de « {{name}} »', { name: channelsFor.name })}
      description={t('aiAgentsUi.bindings.channelsDesc', 'Les messages de ces canaux sont traités par cet agent. Les autres vont à l’agent par défaut.')}
      onClose={closeBindings}
      closeLabel={t('common.close', 'Fermer')}
      footer={(
        <>
          <button type="button" className="u-btn u-btn--secondary" onClick={closeBindings} disabled={binding}>{t('common.cancel', 'Annuler')}</button>
          <button type="button" className="u-btn u-btn--primary" onClick={saveChannels} disabled={binding}>
            {binding ? t('common.saving', 'Enregistrement...') : t('aiAssistant.saveChannels', 'Enregistrer les canaux')}
          </button>
        </>
      )}
    >
      <div className="u-form">
        {bindingError && <Banner tone="danger">{bindingError}</Banner>}
        <ul className="u-list agents-v2__switches">
          {channelRows.map((name) => (
            <li key={name} className="agents-v2__switch-row">
              <Switch
                id={`agent-channel-${name}`}
                checked={Boolean(channelChoice[name])}
                onChange={(checked) => setChannelChoice((previous) => ({ ...previous, [name]: checked }))}
                disabled={binding}
                label={channelLabel(t, name)}
              />
              {!availableChannels[name] && <Badge>{t('aiAgentsUi.bindings.notConnected', 'Non connecté')}</Badge>}
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );

  const renderBrandModal = () => brandFor && (
    <Modal
      icon={Tag}
      title={t('aiAgentsUi.bindings.brandTitle', 'Marque de « {{name}} »', { name: brandFor.name })}
      description={t('aiAgentsUi.bindings.brandDesc', 'Les tickets de cette marque sont traités par cet agent en priorité.')}
      onClose={closeBindings}
      closeLabel={t('common.close', 'Fermer')}
      footer={(
        <>
          <button type="button" className="u-btn u-btn--secondary" onClick={closeBindings} disabled={binding}>{t('common.cancel', 'Annuler')}</button>
          <button type="button" className="u-btn u-btn--primary" onClick={saveBrand} disabled={binding || !brandChoice || brandChoice === brandFor.associated_brand_id}>
            {t('aiAgentsUi.bindings.brandSubmit', 'Associer cette marque')}
          </button>
        </>
      )}
    >
      <div className="u-form">
        {bindingError && <Banner tone="danger">{bindingError}</Banner>}
        <fieldset className="agents-v2__choices">
          <legend className="u-label">{t('aiAgentsUi.bindings.brandLegend', 'Marque')}</legend>
          {brands.map((brand) => (
            <label key={brand.id} className={`agents-v2__choice${brandChoice === brand.id ? ' is-selected' : ''}`}>
              <input type="radio" name="agent-brand" value={brand.id} checked={brandChoice === brand.id} onChange={() => setBrandChoice(brand.id)} disabled={binding} />
              <span className="agents-v2__choice-text">
                {brand.name}
                {brand.description && <span className="u-cell-sub">{brand.description}</span>}
              </span>
              {brandFor.associated_brand_id === brand.id && <Badge tone="success">{t('aiAgentsUi.bindings.current', 'Actuelle')}</Badge>}
            </label>
          ))}
        </fieldset>
      </div>
    </Modal>
  );

  const renderConfirm = () => {
    if (!confirm) return null;
    const { kind, agent } = confirm;
    const email = kind === 'email';
    return (
      <ConfirmDialog
        title={email
          ? t('aiAgentsUi.bindings.removeEmailTitle', 'Retirer {{email}} de « {{name}} » ?', { email: agent.associated_email, name: agent.name })
          : t('aiAgentsUi.bindings.removeBrandTitle', 'Retirer la marque {{brand}} de « {{name}} » ?', { brand: brandName(agent.associated_brand_id) || '', name: agent.name })}
        message={email
          ? t('aiAgentsUi.bindings.removeEmailMessage', 'Les e-mails reçus sur cette adresse ne seront plus confiés en priorité à cet agent.')
          : t('aiAgentsUi.bindings.removeBrandMessage', 'Les tickets de cette marque ne seront plus confiés en priorité à cet agent.')}
        confirmLabel={t('aiAgentsUi.bindings.remove', 'Retirer')}
        cancelLabel={t('common.cancel', 'Annuler')}
        busy={binding}
        icon={Unlink}
        onCancel={() => !binding && setConfirm(null)}
        onConfirm={() => (email ? removeEmail(agent) : removeBrand(agent))}
      />
    );
  };

  const renderCreateModal = () => showCreate && (
    <Modal
      wide
      icon={Bot}
      title={t('aiAgentsUi.create.title', 'Créer un agent')}
      description={t('aiAgentsUi.create.desc', 'Un nom, ses instructions et ses outils. Vous pourrez tout modifier ensuite.')}
      onClose={closeCreate}
      closeLabel={t('common.close', 'Fermer')}
      footer={(
        <>
          <button type="button" className="u-btn u-btn--secondary" onClick={closeCreate} disabled={creating}>{t('common.cancel', 'Annuler')}</button>
          <button type="submit" form="agents-create-form" className="u-btn u-btn--primary" disabled={creating || !newName.trim()}>
            {creating ? t('common.saving', 'Enregistrement...') : t('aiAgentsUi.create.submit', 'Créer l’agent')}
          </button>
        </>
      )}
    >
      <form id="agents-create-form" className="u-form" onSubmit={handleCreate} noValidate>
        {createError && <Banner tone="danger">{createError}</Banner>}
        <Field label={t('aiAgentsUi.create.name', 'Nom *')} htmlFor="agent-new-name">
          <input id="agent-new-name" className="u-input" value={newName} onChange={(e) => setNewName(e.target.value)} disabled={creating} autoFocus />
        </Field>
        <Field
          label={t('aiAgentsUi.create.instructions', 'Instructions')}
          htmlFor="agent-new-prompt"
          help={t('aiAgentsUi.create.instructionsHelp', 'Ce que l’agent doit savoir et faire : votre métier, le ton, quand passer la main.')}
        >
          <textarea id="agent-new-prompt" className="u-textarea agents-v2__prompt" value={newPrompt} onChange={(e) => setNewPrompt(e.target.value)} disabled={creating} />
        </Field>
        <div className="u-field">
          <span className="u-label">{t('aiAgentsUi.create.tools', 'Outils')}</span>
          <ToolChecklist tools={tools} selectedIds={newToolIds} onChange={setNewToolIds} disabled={creating} compact />
        </div>
      </form>
    </Modal>
  );

  if (user === null) {
    return (
      <div className="u-page agents-v2">
        <Banner tone="danger">{t('aiAgentsUi.userError', 'Impossible de charger votre profil.')}</Banner>
      </div>
    );
  }

  return (
    <div className="u-page agents-v2">
      <PageHeader
        title={t('aiAgentsUi.list.title', 'Agents IA')}
        subtitle={subtitle}
        actions={(
          <>
            <button
              type="button"
              className="u-btn u-btn--secondary u-btn--icon"
              onClick={refresh}
              title={t('aiAgentsUi.refresh', 'Actualiser')}
              aria-label={t('aiAgentsUi.refresh', 'Actualiser')}
              disabled={loading || demo}
            >
              <RefreshCw aria-hidden="true" />
            </button>
            <Link className="u-btn u-btn--secondary" to="/knowledge-board">
              <BookOpen aria-hidden="true" />
              {t('aiAssistant.knowledgeBoard', 'Connaissances')}
            </Link>
            <button type="button" className="u-btn u-btn--primary" onClick={openCreate} disabled={loading}>
              <Plus aria-hidden="true" />
              {t('aiAgentsUi.list.create', 'Créer un agent')}
            </button>
          </>
        )}
      />

      <div className="u-well">
        {listError && (
          <Banner
            tone="danger"
            actions={<button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={fetchAgentsAndTools}>{t('aiAgentsUi.retry', 'Réessayer')}</button>}
          >
            {listError}
          </Banner>
        )}
        {renderAttention()}
        {renderKpis()}
        <Card
          icon={Bot}
          title={t('aiAgentsUi.list.cardTitle', 'Vos agents')}
          description={t('aiAgentsUi.list.cardDesc', 'Un message va à l’agent de sa boîte mail, de son canal ou de sa marque ; sinon à l’agent par défaut.')}
          flush
        >
          {renderTable()}
        </Card>
        {!loading && list.length > 0 && (
          <p className="agents-v2__note">
            <ShieldCheck aria-hidden="true" />
            {t('aiAgentsUi.list.note', 'Chaque agent lit les fichiers du ticket, choisit le statut et passe la main à votre équipe avec ses propres outils.')}
          </p>
        )}
      </div>

      {renderEmailModal()}
      {renderChannelsModal()}
      {renderBrandModal()}
      {renderConfirm()}
      {renderCreateModal()}

      {toast && (
        <Toast tone={toast.tone} onClose={() => setToast(null)} closeLabel={t('common.close', 'Fermer')}>
          {toast.text}
        </Toast>
      )}
    </div>
  );
};

// « ⋯ » of a row: separators only between items.
const AgentMenu = ({ items, label }) => {
  const visible = items.filter(Boolean);
  const cleaned = visible.filter((item, index) => !item.separator || (index > 0 && index < visible.length - 1 && !visible[index + 1].separator));
  if (!cleaned.some((item) => !item.separator)) return null;
  return <OverflowMenu items={cleaned} label={label} />;
};

export default AIAgentsList;
